import { EventReward, parseReward } from "./events";

/**
 * The Trophy Hall's data (MP-23, docs/SHOP.md §9.7; balance/src/trophies.json): when the Hall opens and the Armoury's
 * weekly shelf. Trophies are earned only (the event reward key `trophies`: data/events.ts) and never sold; the
 * Treasures are shop.json skins with a `trophies` unlock (meta/trophyHall.ts trophySkins). Parsed from the generated
 * bundle's `trophies` section; an older bundle has none: `emptyTrophies` never opens and sells nothing.
 */

/** One Armoury entry: a reward (essence, a sigil, a chest at the player's level) or `supplies` random pre-battle supplies. */
export interface ArmouryEntry {
  readonly id: string;
  /** Trophies. */
  readonly price: number;
  /** Most bought a week. */
  readonly weeklyCap: number;
  /** The highest cleared level it needs (0 = none); under it the entry shows locked. */
  readonly minLevel: number;
  /** What it pays; null for a supplies pack. */
  readonly reward: EventReward | null;
  /** A supplies pack: this many pre-battle supplies, each picked by (week, entry, purchase) and shown first; 0 otherwise. */
  readonly supplies: number;
}

export interface ArmouryData {
  /** Shown every week, in order. */
  readonly fixed: readonly ArmouryEntry[];
  /** `rotatingSlots` of these a week, picked by the week alone. */
  readonly rotating: readonly ArmouryEntry[];
  readonly rotatingSlots: number;
}

export interface TrophiesData {
  /** The Hall opens once this level is cleared (or as soon as the player holds Trophies). */
  readonly unlockAfterLevel: number;
  readonly armoury: ArmouryData;
}

/** Levels no player reaches: an old bundle's Hall never opens by level. */
const NEVER = 1_000_000;

export function emptyTrophies(): TrophiesData {
  return { unlockAfterLevel: NEVER, armoury: { fixed: [], rotating: [], rotatingSlots: 0 } };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`${where}: expected a whole number >= ${min}, got ${JSON.stringify(v)}`);
}

function entry(e: Json, where: string): ArmouryEntry {
  const id = e?.id;
  if (typeof id !== "string" || id === "") throw new Error(`${where}: id`);
  const w = `${where} ${id}`;
  const hasReward = e.reward !== undefined, hasSupplies = e.supplies !== undefined;
  if (hasReward === hasSupplies) throw new Error(`${w}: exactly one of reward / supplies`);
  return {
    id, price: int(e.price, w, 1), weeklyCap: int(e.weeklyCap, w, 1), minLevel: e.minLevel === undefined ? 0 : int(e.minLevel, w),
    reward: hasReward ? parseReward(e.reward) : null, supplies: hasSupplies ? int(e.supplies, w, 1) : 0,
  };
}

/** Reads the bundle's `trophies` section (gen.py validate_trophies has checked it; this re-checks shape and throws otherwise). */
export function parseTrophies(root: Json): TrophiesData {
  const t = root?.trophies;
  if (t === undefined) return emptyTrophies();
  const a = t.armoury;
  const fixed = (a.fixed as Json[]).map((e) => entry(e, "trophies armoury"));
  const rotating = (a.rotating as Json[]).map((e) => entry(e, "trophies armoury"));
  const ids = [...fixed, ...rotating].map((e) => e.id);
  if (new Set(ids).size !== ids.length) throw new Error("trophies armoury: duplicate id");
  const rotatingSlots = int(a.rotatingSlots, "trophies armoury rotatingSlots");
  if (rotatingSlots > rotating.length) throw new Error("trophies armoury: rotatingSlots above the rotating entries");
  return { unlockAfterLevel: int(t.unlockAfterLevel, "trophies unlockAfterLevel", 1), armoury: { fixed, rotating, rotatingSlots } };
}
